import React, { FC, Suspense, lazy, useState, useId } from 'react';
import { Button } from '@clearcover/atlas';

import { NumericFont } from 'Shared/components';
import { isPayInFullPlan } from 'QuoteApp/utils/paymentPlan';
import { formatMoney } from 'Shared/utils';

import { FlexPaymentRateSection } from './FlexPaymentRateSection';
import {
  getDueTodayLabel,
  getPaymentFrequencyCopy,
  getPaymentPlanAmounts,
} from './utils';
import { FlexPaymentRateProps } from './types';
import styles from './styles.module.css';

const PaymentPlanComparisonModal = lazy(() =>
  import('QuoteApp/components/PaymentPlanComparisonModal').then(module => ({
    default: module.PaymentPlanComparisonModal,
  }))
);

export const FlexPaymentRate: FC<FlexPaymentRateProps> = ({
  paymentPlan,
  disableChangeFrequency,
}) => {
  const sectionIdPrefix = useId();
  const [
    isPaymentPlanComparisonModalOpen,
    setPaymentPlanComparisonModalOpen,
  ] = useState(false);

  const openPaymentPlanComparisonModal = () =>
    setPaymentPlanComparisonModalOpen(true);
  const closePaymentPlanComparisonModal = () =>
    setPaymentPlanComparisonModalOpen(false);

  const isFullPay = isPayInFullPlan(paymentPlan);

  const {
    paymentPlanLabel,
    futurePaymentFrequencyLabel,
  } = getPaymentFrequencyCopy(paymentPlan.paymentFrequencyIdentifier);
  const {
    dueTodayAmount,
    futurePaymentsAmount,
    totalAmount,
  } = getPaymentPlanAmounts(paymentPlan);

  const futurePaymentsLabel = futurePaymentFrequencyLabel
    ? `Paid ${futurePaymentFrequencyLabel}`
    : '';

  return (
    <div className={styles.container} data-testid="flex-payment-rate">
      <FlexPaymentRateSection
        labelId={`${sectionIdPrefix}-payment-plan-label`}
        label="Payment Plan"
        value={<div className={styles.value}>{paymentPlanLabel}</div>}
        footer={
          <Button
            link
            type="button"
            className={styles.changeFrequencyButton}
            data-testid="flex-change-frequency-button"
            disabled={disableChangeFrequency}
            onClick={openPaymentPlanComparisonModal}
          >
            Change Frequency
          </Button>
        }
      />

      <div className={styles.separator} />

      <FlexPaymentRateSection
        labelId={`${sectionIdPrefix}-due-today-label`}
        label="Due Today"
        value={<NumericFont value={dueTodayAmount} className={styles.amount} />}
        detail={
          <div className={styles.detail}>{getDueTodayLabel(paymentPlan)}</div>
        }
      />

      <div className={styles.separator} />

      <FlexPaymentRateSection
        labelId={`${sectionIdPrefix}-future-payments-label`}
        label="Future Payments"
        value={
          <NumericFont
            value={isFullPay ? formatMoney(0) : futurePaymentsAmount}
            className={styles.amount}
          />
        }
        detail={<div className={styles.detail}>{futurePaymentsLabel}</div>}
      />

      <div className={styles.separator} />

      <FlexPaymentRateSection
        labelId={`${sectionIdPrefix}-total-premium-label`}
        label="Total Premium"
        value={<NumericFont value={totalAmount} className={styles.amount} />}
        detail={<div className={styles.detail}>Based on current info</div>}
      />

      {isPaymentPlanComparisonModalOpen && (
        <Suspense fallback={null}>
          <PaymentPlanComparisonModal
            open
            onClose={closePaymentPlanComparisonModal}
          />
        </Suspense>
      )}
    </div>
  );
};
